import React, { useState, useEffect, FC } from "react";
import {
  Shield,
  Scan,
  AlertCircle,
  CheckCircle,
  Zap,
  ScanFace,
  RefreshCcwDot,
  ChevronLeft,
} from "lucide-react";
import Camera from "./FaceVerificationCamera";
import toast from "react-hot-toast";
import {
  ExamsDetailsTypes,
  FaceVerificationPayloadType,
} from "../../interfaces/exams.interface";

import { generateRandomBytes } from "../../utils";

import useUserStore from "../../stores/useUserStores";
import useExamStores from "../../stores/useExamStores";
import useExamServices from "../../context/useExamServices";

interface VerificationData {
  verified: boolean;
  confidence: number;
  faceDetected: boolean;
  attributes: {
    age: number;
    gender: string;
    emotion: string;
    quality: number;
  };
  message: string;
  timestamp: string;
}
type FaceVerificationCameraWrapperProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  successCallback: () => void;
  examDetails: ExamsDetailsTypes;
};
const FaceVerificationCameraWrapper: FC<FaceVerificationCameraWrapperProps> = ({
  open,
  setOpen,
  successCallback,
  examDetails,
}): JSX.Element => {
  const examContext = useExamStores();
  const examServices = useExamServices();
  const userContext = useUserStore();
  const [currentStep, setCurrentStep] = useState<
    "camera" | "processing" | "photo" | "result"
  >("camera");
  const [capturedImage, setCapturedImage] = useState<string>("");
  const [verificationResult, setVerificationResult] =
    useState<VerificationData | null>(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string>("");

  // Check server health on component mount
  //   useEffect(() => {
  //     const checkServer = async () => {
  //       const isOnline = await checkServerHealth();
  //       setServerStatus(isOnline ? 'online' : 'offline');
  //     };

  //     checkServer();
  //     const interval = setInterval(checkServer, 30000); // Check every 30 seconds

  //     return () => clearInterval(interval);
  //   }, []);

  const handleCapture = async (imageData: string) => {
    console.log("imageData >>>> ",imageData);
    
    setCapturedImage(imageData);
    // setIsProcessing(true);
    setCurrentStep("photo");

    setError("");
  };

  const verifyImage = async () => {
    setCurrentStep("processing");
    setIsProcessing(true);
    const encryption_key = generateRandomBytes();
    const payload: FaceVerificationPayloadType = {
      user_photo: capturedImage?.replace("data:image/jpeg;base64,", ""),
      encryption_key,
      exam_group_id: examDetails.exam_group,
      exam_id: examDetails.id,
      user_id: userContext.userdetail.id,
    };

    examContext.setFaceVerified(false);
    await examServices.ApplyScrutinyHandler({
      payload,
      successCallback: () => {
        examContext.setFaceVerified(true);
        successCallback?.();

        toast.success(`Face verification was successfully completed `);
      },
      notFoundCallback: () => {
        toast.error(`Face not  verified, try again!  `);
        examContext.setFaceVerified(false);
      },
      finallyCallback: () => {
        setCurrentStep("camera");
        setIsProcessing(false);
        setCapturedImage("");
      },
    });
  };

  const handleRetry = () => {
    setCurrentStep("camera");
    setCapturedImage("");
    setVerificationResult(null);
    setError("");
    setIsProcessing(false);
  };

  //   const ServerStatusIndicator = () => (
  //     <div className="flex items-center space-x-2 text-sm">
  //       <div className={`w-2 h-2 rounded-full ${
  //         serverStatus === 'online' ? 'bg-green-400' :
  //         serverStatus === 'offline' ? 'bg-red-400' : 'bg-yellow-400'
  //       }`}></div>
  //       <span className="text-gray-400">
  //         Server: {serverStatus === 'checking' ? 'Checking...' : serverStatus}
  //       </span>
  //     </div>
  //   );

  useEffect(() => {
    if (!open) {
      setCapturedImage("");
      setCurrentStep("camera");
      setIsProcessing(false);
      setError("");
    }
  }, [open]);

  if (!open) return <></>;

  return (
    <div className="min-h-screen h-auto  bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white border-t-3 border-white">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(120,119,198,0.3),rgba(255,255,255,0))]"></div>

      <div className="relative z-10  mx-auto px-4">
        {/* Header */}
        <div className="mt-2">
          <div
            className="flex cursor-pointer gap-x-2 group pl-3  rounded-md"
            onClick={() => setOpen(false)}
          >
            <ChevronLeft />
            <p className="group-hover:underline underline-offset-2">Go Back</p>
          </div>
        </div>

        <div className="text-center mb-2 flex items-center relative mt-10 lg:mt-1">
          <div className="flex items-center justify-center space-x-3 mb-2 w-full">
            <div className="p-1 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <h1 className="md:text-2xl text-lg  font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Face Recognition System
            </h1>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="max-w-md mx-auto mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <p className="text-red-300">{error}</p>
            </div>
          </div>
        )}
        {/* Main Content */}
        <main className="lg:mt-3 mt-10 xl:mt-[20px]">
          {currentStep === "camera" && (
            <div className="">
              <div className="text-center">
                <h2 className="md:text-xl text-md font-semibold">
                  Position Your Face
                </h2>
                <p className="text-gray-300 md:text-base text-sm">
                  Look directly at the camera and ensure good lighting for best
                  results
                </p>
              </div>
              <div className="">
                <Camera
                  onCapture={handleCapture}
                  isProcessing={isProcessing}
                  handleRetry={handleRetry}
                  cameraOpen={open}
                />
              </div>
            </div>
          )}

          {/* {currentStep === "processing" && ( */}
          <div className="flex ">
            {/* <div className="flex items-center justify-center">
              <div className="relative">
                <div className="w-24 h-24 border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <Scan className="w-8 h-8 text-cyan-400" />
                </div>
              </div>
            </div> */}

            {/* <div className="space-y-2">
              <h2 className="text-2xl font-semibold">Analyzing Face...</h2>
              <p className="text-gray-300">
                Processing biometric data for verification
              </p>
            </div> */}

            {capturedImage && (
              <div className="text-center  w-full relative  mt-10">
                <div className="mx-auto w-full flex items-center justify-center  relative ">
                  <img
                    src={capturedImage}
                    alt="Processing"
                    className="w-[750px] h-[450px] rounded-xl border-[7px] border-green-500 opacity-100 "
                  />
                  {isProcessing ? (
                    <div className="absolute top-[40%]">
                      <div className="relative">
                        <div className="w-24 h-24 border-4 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Scan className="w-8 h-8 text-cyan-400" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
                <div className="mt-8 items-center gap-x-10 justify-center flex-col md:flex-row gap-y-5 md:gap-y-0 flex ">
                  {!isProcessing ? (
                    <button
                      onClick={handleRetry}
                      disabled={isProcessing || !!error}
                      // onClick={capturePhoto}
                      // disabled={isProcessing || isLoading || !!error}
                      className="relative order-2 md:order-1  px-16  md:px-8 py-2 bg-transparent border border-blue-500 disabled:bg-gradient-to-r  disabled:border-transparent disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold rounded-full shadow-lg transition-all duration-200 hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed"
                    >
                      <div className="flex items-center space-x-2">
                        <RefreshCcwDot className="w-5 h-5 text-white" />
                        <span>Retake</span>
                      </div>
                    </button>
                  ) : (
                    <></>
                  )}

                  <button
                    onClick={verifyImage}
                    disabled={isProcessing || !!error}
                    className="relative px-8 py-2 bg-gradient-to-r  from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold rounded-full shadow-lg transition-all duration-200 hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed"
                  >
                    <div className="flex items-center  justify-center space-x-2">
                      <ScanFace className="w-5 h-5" />
                      <span>Verify Your Face</span>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* )} */}

          {/* {currentStep === 'result' && verificationResult && (
            <VerificationResult
              result={verificationResult}
              capturedImage={capturedImage}
              onRetry={handleRetry}
            />
          )} */}
        </main>
      </div>
    </div>
  );
};

export default FaceVerificationCameraWrapper;
